import type { AxiosInstance } from 'axios';
import { internalApiClient } from '@/marketing/infrastructure/http/httpClient';
import type {
  CommissionTierRates,
  GenerateVouchersRequest,
  GenerateVouchersResult,
  ImportVouchersRequest,
  ImportVouchersResult,
  SetCommissionTierRatesRequest,
  SetVoucherCommissionRequest,
  SetVoucherStatusRequest,
  VoucherListParams,
  VoucherListing,
  VoucherMutationResult,
  VoucherPerformance,
} from '@/marketing/models/voucher';
import { mockVoucherAPIClient } from './mockVoucherAPIClient';

// COUPON is an internal-api domain (ApiRouting.ts). Unlike the community domain, which answers
// under an /internal prefix, the coupon controllers are mounted at the bare `coupon` path —
// verified on dev, where /coupon/influencer authenticates and /internal/coupon/influencer 404s.
const BASE_PATH = '/coupon/influencer';
const TIER_RATES_PATH = '/influencer/commission/tiers';

export interface VoucherAPI {
  listVouchers(params: VoucherListParams): Promise<VoucherListing>;
  getPerformance(code: string): Promise<VoucherPerformance>;
  generateVouchers(request: GenerateVouchersRequest): Promise<GenerateVouchersResult>;
  importVouchers(request: ImportVouchersRequest): Promise<ImportVouchersResult>;
  setVoucherStatus(request: SetVoucherStatusRequest): Promise<VoucherMutationResult>;
  setVoucherCommission(request: SetVoucherCommissionRequest): Promise<VoucherMutationResult>;
  getCommissionTierRates(): Promise<CommissionTierRates>;
  setCommissionTierRates(request: SetCommissionTierRatesRequest): Promise<CommissionTierRates>;
}

export class VoucherAPIClient implements VoucherAPI {
  constructor(private readonly client: AxiosInstance = internalApiClient) {}

  public async listVouchers(params: VoucherListParams): Promise<VoucherListing> {
    const { data } = await this.client.get<VoucherListing>(BASE_PATH, { params });
    return data;
  }

  public async getPerformance(code: string): Promise<VoucherPerformance> {
    const { data } = await this.client.get<VoucherPerformance>(`${BASE_PATH}/${encodeURIComponent(code)}/performance`);
    return data;
  }

  public async generateVouchers(request: GenerateVouchersRequest): Promise<GenerateVouchersResult> {
    const { data } = await this.client.post<GenerateVouchersResult>(`${BASE_PATH}/generate`, request);
    return data;
  }

  public async importVouchers(request: ImportVouchersRequest): Promise<ImportVouchersResult> {
    const { data } = await this.client.post<ImportVouchersResult>(`${BASE_PATH}/import`, request);
    return data;
  }

  public async setVoucherStatus({ code, status }: SetVoucherStatusRequest): Promise<VoucherMutationResult> {
    const { data } = await this.client.patch<VoucherMutationResult>(`${BASE_PATH}/${encodeURIComponent(code)}/status`, {
      status,
    });
    return data;
  }

  // The PATCH replaces the code's whole override set: a rate left out of the body clears
  // that source's override, so only the rates that should stay overridden are sent.
  public async setVoucherCommission({
    code,
    commissionPercent,
    utmPercent,
    couponUtmPercent,
  }: SetVoucherCommissionRequest): Promise<VoucherMutationResult> {
    const { data } = await this.client.patch<VoucherMutationResult>(
      `${BASE_PATH}/${encodeURIComponent(code)}/commission`,
      {
        ...(commissionPercent !== undefined ? { commissionPercent } : {}),
        ...(utmPercent !== undefined ? { utmPercent } : {}),
        ...(couponUtmPercent !== undefined ? { couponUtmPercent } : {}),
      },
    );
    return data;
  }

  public async getCommissionTierRates(): Promise<CommissionTierRates> {
    const { data } = await this.client.get<CommissionTierRates>(TIER_RATES_PATH);
    return data;
  }

  public async setCommissionTierRates(request: SetCommissionTierRatesRequest): Promise<CommissionTierRates> {
    const { data } = await this.client.put<CommissionTierRates>(TIER_RATES_PATH, request);
    return data;
  }
}

// VITE_AFFILIATE_MOCKS=true serves the screen from an in-memory stand-in for the internal-api
// COUPON endpoints. Dropping the flag switches to the real internal-api.
const useAffiliateMocks = import.meta.env.DEV && import.meta.env['VITE_AFFILIATE_MOCKS'] === 'true';

export const voucherAPIClient: VoucherAPI = useAffiliateMocks ? mockVoucherAPIClient : new VoucherAPIClient();
